// One definition of "is this SMS template safe to send", shared by the API
// (rejects on save/activation) and the editors (live counter + inline errors).

export const SMS_BRAND_NAME = "Care team";
export const SMS_OPT_OUT_TEXT = "Reply STOP to opt out";

/** What a typical recipient would see; templates are measured after substitution. */
const SAMPLE_VALUES: Record<string, string> = {
  first_name: "Maria",
  specialty: "dental care",
};

const GSM7_BASIC = new Set(
  "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà",
);
// Each costs two septets (escape + char).
const GSM7_EXTENDED = new Set("^{}\\[~]|€\f");

export type SmsEncoding = "GSM-7" | "UCS-2";

export type SmsTemplateStats = {
  /** Septets (GSM-7) or UTF-16 code units (UCS-2) after sample substitution. */
  length: number;
  encoding: SmsEncoding;
  segments: number;
};

export const renderSmsSample = (template: string): string =>
  template.replace(/\{([a-z_]+)\}/g, (match, token: string) => SAMPLE_VALUES[token] ?? match);

/** Greedy packing: a two-unit character (extended GSM, surrogate pair) never straddles parts. */
const pack = (costs: number[], single: number, multi: number): number => {
  const total = costs.reduce((sum, cost) => sum + cost, 0);
  if (total === 0) return 0;
  if (total <= single) return 1;
  let parts = 1;
  let used = 0;
  for (const cost of costs) {
    if (used + cost > multi) {
      parts += 1;
      used = 0;
    }
    used += cost;
  }
  return parts;
};

export const smsTemplateStats = (template: string): SmsTemplateStats => {
  const text = renderSmsSample(template);
  const chars = [...text];
  const gsm = chars.every(char => GSM7_BASIC.has(char) || GSM7_EXTENDED.has(char));
  if (gsm) {
    const costs = chars.map(char => (GSM7_EXTENDED.has(char) ? 2 : 1));
    return {
      length: costs.reduce((sum, cost) => sum + cost, 0),
      encoding: "GSM-7",
      segments: pack(costs, 160, 153),
    };
  }
  const costs = chars.map(char => char.length);
  return {
    length: text.length,
    encoding: "UCS-2",
    segments: pack(costs, 70, 67),
  };
};

export type SmsTemplateProblem = "brand" | "optOut";

export const smsTemplateProblems = (template: string, senderName: string = SMS_BRAND_NAME): SmsTemplateProblem[] => {
  const problems: SmsTemplateProblem[] = [];
  if (!template.toLowerCase().includes(senderName.toLowerCase())) problems.push("brand");
  if (!/\bstop\b/i.test(template)) problems.push("optOut");
  return problems;
};

export const smsTemplateProblemMessage = (label: string, senderName: string = SMS_BRAND_NAME): string =>
  `${label} must include "${senderName}" and "${SMS_OPT_OUT_TEXT}"`;
